"use client";

import { Check } from "lucide-react";
import { PRICING_PLANS } from "@/lib/constants";
import { AnimateOnScroll } from "@/components/shared/AnimateOnScroll";
import { AppStoreBadges } from "@/components/shared/AppStoreBadges";
import {
  StaggerContainer,
  StaggerItem,
} from "@/components/shared/StaggerContainer";

// Purchases happen in the iOS and Android apps only (no web checkout yet), so
// the section points at the stores instead of offering a buy button.
export function Pricing() {
  return (
    <section id="pricing" className="scroll-mt-16 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <AnimateOnScroll>
          <h2 className="text-center text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Simple pricing
          </h2>
        </AnimateOnScroll>
        <AnimateOnScroll delay={0.15}>
          <p className="mx-auto mt-5 max-w-2xl text-center text-xl text-muted-foreground sm:text-2xl">
            Start free. Try the Guided plan free for 7 days, or unlock every
            recommendation with a one-time purchase.
          </p>
        </AnimateOnScroll>

        <StaggerContainer
          className="mx-auto mt-16 grid max-w-6xl gap-6 md:grid-cols-3 md:gap-8"
          stagger={0.12}
        >
          {PRICING_PLANS.map((plan) => (
            <StaggerItem key={plan.name} className="h-full">
              <div
                className={`relative flex h-full flex-col rounded-2xl bg-card p-8 shadow-[var(--shadow-sm)] ${
                  plan.highlighted
                    ? "border-2 border-primary shadow-[var(--shadow-md)]"
                    : "border border-border"
                }`}
              >
                {plan.badge && (
                  <span className="mb-3 self-start rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                    {plan.badge}
                  </span>
                )}
                <h3 className="text-2xl font-bold text-foreground">
                  {plan.name}
                </h3>
                <p className="mt-2 text-base text-muted-foreground">
                  {plan.summary}
                </p>
                <p className="mt-6 text-foreground">
                  <span className="text-4xl font-bold tracking-tight">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-lg font-medium text-muted-foreground">
                      {plan.period}
                    </span>
                  )}
                </p>
                {plan.altPrice && (
                  <p className="mt-1 text-base text-muted-foreground">
                    {plan.altPrice}
                  </p>
                )}
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-3 text-base text-foreground"
                    >
                      <Check
                        className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.note && (
                  <p className="mt-6 text-sm text-muted-foreground">
                    {plan.note}
                  </p>
                )}
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <AnimateOnScroll delay={0.1}>
          <div className="mt-12 text-center">
            <p className="text-lg text-foreground">
              Start your plan in the iPhone or Android app.
            </p>
            <div className="mt-6">
              <AppStoreBadges placement="pricing" size="md" />
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              US prices; may vary by country and app store.
            </p>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
}
